// // components/InternshipDialog.tsx
import InputField from "@/components/form/components/InputField";
import TextareaField from "@/components/form/components/Textarea";
import SelectField from "@/components/form/components/SelectField";
import MultiSelectField from "@/components/form/components/MultiSelectField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateInternshipMutation,
  useUpdateInternshipMutation,
} from "@/hooks/api/internship.queries";
import {
  INTERNSHIP_DOMAINS,
  InternshipCreateType,
  InternshipDomain,
  InternshipParamsType,
  InternshipType,
} from "@/services/internship.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type Props = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: InternshipParamsType;
  internshipId?: string;
  internshipData?: InternshipType | null;
  onCancel: () => void;
  isMulti?: boolean;
};

// Radix cannot render an option with an empty value, so the blank choice
// carries a sentinel that is mapped back to `null` on submit.
const DOMAIN_NOT_SPECIFIED = "__not_specified__";

const DOMAIN_OPTIONS = [
  { label: "Not specified", value: DOMAIN_NOT_SPECIFIED },
  ...INTERNSHIP_DOMAINS.map((domain) => ({ label: domain, value: domain })),
];

const isKnownDomain = (value?: string | null): value is InternshipDomain =>
  !!value && (INTERNSHIP_DOMAINS as readonly string[]).includes(value);

const schema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  type: z.string().min(1, "Type is required"),
  domain: z.union([
    z.literal(""),
    z.literal(DOMAIN_NOT_SPECIFIED),
    z.enum([...INTERNSHIP_DOMAINS]),
  ]),
  location: z.string().optional(),
  is_remote: z.enum(["true", "false"]),
  stipend_min: z.string(),
  stipend_max: z.string(),
  openings: z.string(),
  duration_weeks: z.string(),
  start_date: z.string(),
  application_deadline: z.string(),
  required_skills: z.array(z.string()).min(1, "At least one skill is required"),
  requires_partnership: z.enum(["true", "false"]),
  target_degree_types: z.array(z.string()).min(1, "At least one degree is required"),
}).refine((data) => {
  if (data.is_remote === "false" && (!data.location || data.location.trim() === "")) {
    return false;
  }
  return true;
}, {
  message: "Location is required if not remote",
  path: ["location"],
});

type FormType = z.infer<typeof schema>;

const InternshipDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  internshipId,
  internshipData,
  onCancel,
}: Props) => {

  const createMutation = useCreateInternshipMutation(filters);
  const updateMutation = useUpdateInternshipMutation(filters);
  const [skillInput, setSkillInput] = useState("");
  // Set when an existing posting carries a domain the dropdown cannot offer.
  const [legacyDomain, setLegacyDomain] = useState<string | null>(null);

  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      type: "internship",
      domain: "",
      location: "",
      is_remote: "false",
      stipend_min: "0",
      stipend_max: "0",
      openings: "1",
      duration_weeks: "1",
      start_date: "",
      application_deadline: "",
      required_skills: [],
      requires_partnership: "false",
      target_degree_types: [],
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && internshipData) {
        const incomingDomain = internshipData.domain;
        const isUnsupportedDomain =
          !!incomingDomain && !isKnownDomain(incomingDomain);
        setLegacyDomain(isUnsupportedDomain ? incomingDomain : null);

        form.reset({
          title: internshipData.title || "",
          description: internshipData.description || "",
          type: internshipData.type || "internship",
          // Unsupported values leave the dropdown unselected; a missing value is
          // a legitimate "unclassified" posting, so it shows as "Not specified".
          domain: isKnownDomain(incomingDomain)
            ? incomingDomain
            : isUnsupportedDomain
              ? ""
              : DOMAIN_NOT_SPECIFIED,
          location: internshipData.location || "",

          is_remote: internshipData.is_remote ? "true" : "false",

          stipend_min: String(internshipData.stipend_min ?? 0),
          stipend_max: String(internshipData.stipend_max ?? 0),

          openings: String(internshipData.openings ?? 1),
          duration_weeks: String(internshipData.duration_weeks ?? 1),

          start_date: internshipData.start_date
            ? String(internshipData.start_date).split("T")[0]
            : "",
          application_deadline: internshipData.application_deadline
            ? String(internshipData.application_deadline).split("T")[0]
            : "",

          required_skills:
            internshipData.required_skills || [],

          requires_partnership: internshipData.requires_partnership
            ? "true"
            : "false",

          target_degree_types:
            internshipData.target_degree_types || [],
        });
      } else if (mode === "create") {
        setLegacyDomain(null);
        form.reset({
          title: "",
          description: "",
          type: "internship",
          domain: "",
          location: "",
          is_remote: "false",
          stipend_min: "0",
          stipend_max: "0",
          openings: "1",
          duration_weeks: "1",
          start_date: "",
          application_deadline: "",
          required_skills: [],
          requires_partnership: "false",
          target_degree_types: [],
        });
        setSkillInput("");
      }
    }
  }, [isOpen, mode, internshipData, form]);

  const watchedLocation = form.watch("location");
  const watchedIsRemote = form.watch("is_remote");

  // Effect to handle field inter-dependencies
  useEffect(() => {
    if (watchedIsRemote === "true") {
      form.setValue("location", "");
    }
  }, [watchedIsRemote, form]);

  const currentSkills = form.watch("required_skills") || [];

  const handleAddSkill = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    if (!skillInput.trim()) return;
    if (!currentSkills.includes(skillInput.trim())) {
      form.setValue("required_skills", [...currentSkills, skillInput.trim()], { shouldValidate: true });
    }
    setSkillInput("");
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    form.setValue("required_skills", currentSkills.filter((s) => s !== skillToRemove), { shouldValidate: true });
  };


  const handleSubmit = (values: FormType) => {
    // Legacy postings must be reclassified before they can be saved at all:
    // the API would reject the old value with a 422 the recruiter did not cause.
    if (legacyDomain && !isKnownDomain(values.domain)) {
      form.setError("domain", {
        type: "manual",
        message: "Please pick a domain before saving.",
      });
      return;
    }

    const payload: InternshipCreateType = {
      title: values.title,
      description: values.description || "",
      type: values.type,
      domain: isKnownDomain(values.domain) ? values.domain : null,
      location: values.location || "",
      is_remote: values.is_remote === "true",
      stipend_min: Number(values.stipend_min),
      stipend_max: Number(values.stipend_max),
      openings: Number(values.openings),
      duration_weeks: Number(values.duration_weeks),
      start_date: values.start_date,
      application_deadline: new Date(values.application_deadline).toISOString(),
      required_skills: values.required_skills,
      requires_partnership: values.requires_partnership === "true",
      target_degree_types: values.target_degree_types || [],
    };


    if (mode === "create") {
      createMutation.mutate(payload, {
        onSuccess: () => onCancel(),
      });
    } else if (mode === "edit" && internshipId) {
      updateMutation.mutate(
        { _id: internshipId, data: payload },
        {
          onSuccess: () => onCancel(),
        }
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create Internship" : "Edit Internship"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid gap-4 md:grid-cols-2"
          >

            <InputField name="title" label="Title" type="text" placeholder="Ex: React.Js" required />
            <SelectField
              placeholder="Select Type"
              name="type"
              label="Type"
              options={[
                { label: "Internship", value: "internship" },
                { label: "Full-Time", value: "full-time" },
                { label: "Part-time", value: "part-time" },
              ]}
            />

            <SelectField
              name="domain"
              label="Domain"
              placeholder="Select domain"
              options={
                legacyDomain
                  ? DOMAIN_OPTIONS.filter((o) => o.value !== DOMAIN_NOT_SPECIFIED)
                  : DOMAIN_OPTIONS
              }
              required={!!legacyDomain}
              description={
                legacyDomain
                  ? `Current value '${legacyDomain}' is no longer supported — please pick one.`
                  : "Used by the student app filters."
              }
            />
            <InputField
              name="location"
              label="Location"
              type="text"
              placeholder="Chennai"
              disabled={watchedIsRemote === "true"}
              required={watchedIsRemote === "false"}
            />

            <SelectField
              name="is_remote"
              label="Remote?"
              options={[
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
              ]}
              disabled={!!watchedLocation && watchedLocation.trim().length > 0}
              required
            />

            <InputField name="stipend_min" type="number" label="Min Stipend" required />
            <InputField name="stipend_max" type="number" label="Max Stipend" required />
            <InputField name="openings" type="number" label="Openings" required />
            <InputField name="duration_weeks" type="number" label="Duration (weeks)" required />
            <InputField name="start_date" type="date" label="Start Date" required />
            <InputField name="application_deadline" type="date" label="Deadline" required />

            <TextareaField
              name="description"
              label="Description"
              placeholder="Ex: We need basic Knowledge"
              className={{ itemClass: "md:col-span-2" }}
              required
            />

            <div className="md:col-span-2 space-y-2">
              <label className="text-sm font-medium">Required Skills <span className="text-red-500">*</span></label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Type a skill and click Add or press Enter"
                />
                <Button type="button" onClick={handleAddSkill}>
                  Add
                </Button>
              </div>
              {form.formState.errors.required_skills && (
                <p className="text-sm text-destructive">{form.formState.errors.required_skills.message}</p>
              )}
              {currentSkills.length > 0 && (
                <div className="flex flex-wrap gap-2 rounded-md border p-3 border-input bg-secondary/10 min-h-12.5">
                  {currentSkills.map((skill) => (
                    <div
                      key={skill}
                      className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-sm font-medium text-primary"
                    >
                      <span>{skill}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(skill)}
                        className="text-primary hover:text-destructive flex items-center justify-center p-0.5 rounded-full hover:bg-secondary/20 ml-1 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <MultiSelectField
              name="target_degree_types"
              label="Degrees"
              placeholder="Select Degrees"
              options={[
                { label: "UG", value: "ug" },
                { label: "PG", value: "pg" },
              ]}
              required
            />

            <DialogFooter className="md:col-span-2">
              <Button type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {mode === "create" ? "Create" : "Update"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InternshipDialog;


